import { useState } from "react";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, RefreshCw } from "lucide-react";

const randomPassword = () => Math.random().toString(36).slice(-4) + Math.random().toString(36).slice(-4) + "A9";

export const SetPasswordDialog = ({ customer, onClose, onSetPassword }) => {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await onSetPassword(customer.id, password);
      toast.success("Password updated. Share it with the customer securely.");
      setPassword("");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const copy = () => { navigator.clipboard.writeText(password); toast.success("Password copied"); };

  return (
    <Dialog open={!!customer} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Set password — {customer?.name || customer?.email}</DialogTitle>
          <DialogDescription>
            For security, original passwords cannot be viewed. Set a new password here and share it with the customer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-600">New password</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="rounded-sm font-mono"
                data-testid="set-password-input"
              />
              <Button type="button" variant="outline" onClick={() => setPassword(randomPassword())} title="Generate" className="rounded-sm px-3" data-testid="set-password-generate">
                <RefreshCw className="w-4 h-4" strokeWidth={1.5}/>
              </Button>
              <Button type="button" variant="outline" onClick={copy} disabled={!password} title="Copy" className="rounded-sm px-3" data-testid="set-password-copy">
                <Copy className="w-4 h-4" strokeWidth={1.5}/>
              </Button>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#002fa7] hover:bg-[#00227b] rounded-sm" data-testid="set-password-submit">
              {saving ? "Saving..." : "Update password"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
